import React, { useState } from "react"
import moment from "moment"
import { useAppState, useAppNavigation } from "@hooks/index"
import { useSelector } from "react-redux"

import { useGetAllVehiclesQuery } from "@features/vehicle/vehicles-slice"
import { returnDropDownItems } from "./helper"
import { Dropdown } from "@components/drop-down"
import { PageSkeleton } from "@components/skeleton"
import {
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  Chip,
  Stack,
  TextField,
  Typography,
  FormHelperText,
  FormLabel,
  Button,
} from "@mui/material"
import LoadingButton from "@mui/lab/LoadingButton"

import { EDIT_VEHICLE_ROUTE } from "@features/vehicle"
import { ADMIN_ROUTE } from "../index"

const Admin = () => {
  const [createdAt, setCreatedAt] = useState("")
  const [showButton, setShowButton] = useState(true)
  const [regNo, setRegNo] = useState("")
  const [email, setEmail] = useState("")
  const [expiry, setExpiry] = useState("")
  const [value, setValue] = useState("")
  const [key, setKey] = useState("email")
  const { saveFn } = useAppState()
  const { show } = useAppNavigation()

  const { data, isFetching } = useGetAllVehiclesQuery({
    createdAt,
    key: key,
    value,
  })
  const allVehicles = useSelector(
    (state: any) => state.global.list?.AllVehicles
  )

  const handleMoreVehicles = (vehicles: any) => {
    if (data?.length > 99) {
      const newCreatedAt = vehicles[vehicles.length - 1].createdAt
      setCreatedAt(newCreatedAt)
    } else {
      setShowButton(false)
    }
  }

  const handleEditVehicle = (vehicle: any) => {
    saveFn({ editVehicle: vehicle })
    show(EDIT_VEHICLE_ROUTE, {
      redirect: ADMIN_ROUTE,
      approved: true,
      isAdmin: true,
    })
  }

  const handleSearchByEmail = async () => {
    setKey("email")
    setValue(email)
    setRegNo("")
    setExpiry("")
    setCreatedAt("")
  }

  const handleSearchByRegNo = async () => {
    setKey("regNo")
    setValue(regNo)
    setEmail("")
    setExpiry("")
    setCreatedAt("")
  }

  const displayedDocs = ["vl", "rw", "ins"]

  const returnExpiryDays = (expiryDate: string) => {
    if (!expiryDate) return 0

    const today = moment()
    const expiry = moment(expiryDate, "YYYY-MM-DD")
    return expiry.diff(today, "days")
  }

  return (
    <>
      {isFetching && !allVehicles?.length ? (
        <PageSkeleton />
      ) : (
        <Stack spacing={2}>
          <Stack spacing={2}>
            <Typography> Sort by:</Typography>
            <Stack
              direction={["column", "row"]}
              gap={2}
              sx={{ alignItems: "flex-end" }}
            >
              <FormLabel>
                <FormHelperText> Registration number </FormHelperText>
                <TextField
                  color="primary"
                  name="regNo"
                  placeholder="Reg. no"
                  onChange={(e) => setRegNo(e.target.value.toUpperCase())}
                  value={regNo}
                />
              </FormLabel>
              <FormLabel>
                <FormHelperText> Email address </FormHelperText>
                <TextField
                  color="primary"
                  name="email"
                  placeholder="Email"
                  onChange={(e) => setEmail(e.target.value.toLowerCase())}
                  value={email}
                />
              </FormLabel>
              <FormLabel>
                <FormHelperText> Maximum expiry days </FormHelperText>
                <TextField
                  color="primary"
                  name="expiry"
                  type="number"
                  placeholder="Expiry date"
                  onChange={(e) => setExpiry(e.target.value)}
                  value={expiry}
                />
              </FormLabel>
              <Stack gap={1} sx={{ justifyContent: "space-between" }}>
                <Typography sx={{ fontWeight: "bold" }}>Search by: </Typography>
                <Stack direction="row" gap={1}>
                  <LoadingButton
                    size="small"
                    color="primary"
                    variant="contained"
                    loading={isFetching}
                    sx={{ minWidth: "max-content", whiteSpace: "no-wrap" }}
                    onClick={handleSearchByRegNo}
                  >
                    Reg No.
                  </LoadingButton>
                  <LoadingButton
                    size="small"
                    color="primary"
                    variant="contained"
                    loading={isFetching}
                    sx={{ minWidth: "max-content", whiteSpace: "no-wrap" }}
                    onClick={handleSearchByEmail}
                  >
                    Email
                  </LoadingButton>
                </Stack>
              </Stack>
            </Stack>
          </Stack>
          <TableContainer component={Paper}>
            <Table
              sx={{ minWidth: 650 }}
              size="small"
              aria-label="a dense table"
            >
              <TableHead>
                <TableRow>
                  <TableCell>#</TableCell>
                  <TableCell>Vehicle</TableCell>
                  <TableCell>Name </TableCell>
                  <TableCell>Email </TableCell>
                  <TableCell>Reg. </TableCell>
                  {displayedDocs.map((doc, i) => (
                    <TableCell key={i}> {doc.toUpperCase()} </TableCell>
                  ))}
                  <TableCell></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data && allVehicles?.length
                  ? allVehicles
                      .filter((vehicle: any) =>
                        vehicle.regNo
                          .toLowerCase()
                          .includes(regNo.toLowerCase())
                      )
                      .filter((vehicle: any) =>
                        vehicle?.email
                          .toLowerCase()
                          .includes(email.toLowerCase())
                      )
                      .filter((vehicle: any) =>
                        vehicle.documents.some(
                          (docs: any) =>
                            returnExpiryDays(docs.expiryDate) <=
                              Number(expiry) * -1 || Number(expiry) === 0
                        )
                      )
                      .map((vehicle: any, i: number) => (
                        <TableRow
                          key={vehicle.vid}
                          sx={{
                            "&:last-child td, &:last-child th": { border: 0 },
                          }}
                        >
                          <TableCell>{i + 1}</TableCell>
                          <TableCell component="th" scope="row">
                            {`${vehicle.make} ${vehicle.model}`}
                          </TableCell>
                          <TableCell>{vehicle?.name}</TableCell>
                          <TableCell>{vehicle?.email}</TableCell>
                          <TableCell>{vehicle.regNo || "N/A"}</TableCell>

                          {vehicle.documents.map((docs: any, i: string) => {
                            const days = returnExpiryDays(docs.expiryDate)
                            return displayedDocs.includes(docs.key) ? (
                              <TableCell key={i}>
                                {vehicle?.regNo && days ? (
                                  <Chip
                                    size="small"
                                    color={days > 15 ? "success" : "error"}
                                    label={days}
                                    sx={{ fontSize: 10 }}
                                  />
                                ) : null}
                              </TableCell>
                            ) : null
                          })}
                          <TableCell size="small">
                            <Dropdown
                              items={returnDropDownItems(
                                vehicle,
                                handleEditVehicle
                              )}
                            />
                          </TableCell>
                        </TableRow>
                      ))
                  : null}
              </TableBody>
            </Table>
          </TableContainer>
          {showButton && !isFetching && data?.length > 99 ? (
            <Button onClick={() => handleMoreVehicles(data)}>
              Load more vehicles
            </Button>
          ) : null}
        </Stack>
      )}
    </>
  )
}

export default Admin
